import { Capacitor } from "@capacitor/core";

const ZUNO_BUNDLE_ID = "com.zuno.passenger";
const FIREBASE_GOOGLE_API_HOSTS = new Set([
  "firebase.googleapis.com",
  "firebaseappcheck.googleapis.com",
  "firebaseinstallations.googleapis.com",
  "firebasestorage.googleapis.com",
  "firestore.googleapis.com",
  "identitytoolkit.googleapis.com",
  "securetoken.googleapis.com",
]);
const FETCH_GUARD = Symbol.for("zuno.firebasePlatformSecurity.fetchGuard");

const normalizeFingerprint = (value) => String(value || "").replace(/[^0-9a-f]/gi, "").toUpperCase();

const requestUsesFirebaseKey = (input, firebaseApiKey) => {
  try {
    const rawUrl = typeof input === "string" || (typeof URL !== "undefined" && input instanceof URL)
      ? String(input)
      : input?.url;
    const url = new URL(rawUrl);
    return FIREBASE_GOOGLE_API_HOSTS.has(url.hostname)
      && url.searchParams.get("key") === firebaseApiKey;
  } catch {
    return false;
  }
};

const platformHeaders = (platform, androidCertSha1, androidCertSha256) => {
  if (platform === "ios") return { "X-Ios-Bundle-Identifier": ZUNO_BUNDLE_ID };
  if (platform === "android") {
    const normalizedSha1 = normalizeFingerprint(androidCertSha1);
    const normalizedSha256 = normalizeFingerprint(androidCertSha256);
    if (normalizedSha1.length !== 40 || normalizedSha256.length !== 64) {
      throw new Error("Zuno Android Firebase security requires release signing SHA-1 and SHA-256 fingerprints.");
    }
    return {
      "X-Android-Package": ZUNO_BUNDLE_ID,
      "X-Android-Cert": normalizedSha1,
    };
  }
  return {};
};

export function configureFirebasePlatformSecurity({
  platform = Capacitor.getPlatform(),
  native = Capacitor.isNativePlatform(),
  firebaseApiKey = process.env.REACT_APP_FIREBASE_API_KEY,
  buildPlatform = process.env.REACT_APP_FIREBASE_NATIVE_PLATFORM || "web",
  androidCertSha1 = process.env.REACT_APP_FIREBASE_ANDROID_CERT_SHA1,
  androidCertSha256 = process.env.REACT_APP_FIREBASE_ANDROID_CERT_SHA256,
  target = window,
} = {}) {
  if (!native) return { mode: "web-referrer-restriction" };
  if (!firebaseApiKey) throw new Error("Zuno Firebase API key is missing from the native build.");
  if (platform !== "ios" && platform !== "android") throw new Error(`Unsupported native platform: ${platform}`);
  if (buildPlatform !== platform) {
    throw new Error(`Zuno Firebase native key mismatch: this ${platform} app was not built with its platform key.`);
  }
  if (target[FETCH_GUARD]) return { mode: `${platform}-application-restriction` };
  if (typeof target.fetch !== "function") throw new Error("Fetch is unavailable for Firebase platform protection.");

  const originalFetch = target.fetch.bind(target);
  const identityHeaders = platformHeaders(platform, androidCertSha1, androidCertSha256);
  target.fetch = (input, init = {}) => {
    if (!requestUsesFirebaseKey(input, firebaseApiKey)) return originalFetch(input, init);
    const sourceHeaders = typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined;
    const headers = new Headers(sourceHeaders);
    new Headers(init.headers).forEach((value, name) => headers.set(name, value));
    Object.entries(identityHeaders).forEach(([name, value]) => headers.set(name, value));
    return originalFetch(input, { ...init, headers });
  };
  Object.defineProperty(target, FETCH_GUARD, { value: true, configurable: false });
  return { mode: `${platform}-application-restriction` };
}

export const firebasePlatformSecurityConstants = Object.freeze({
  bundleId: ZUNO_BUNDLE_ID,
  allowedHosts: [...FIREBASE_GOOGLE_API_HOSTS],
});
